import { analytics } from './segment';
import {
  buildFeedInteractionPayload,
  type FeedInteractionFields,
} from './types/FeedInteractionFields';
import { resolveInteractionTab } from './utils/resolveInteractionTab';
import { getTabSessionIdForTab } from './utils/tabSession';

/** Cookbook cards only surface a view or a tap — no cart/save actions. */
type CookbookCardAction = 'viewed' | 'clicked';

/**
 * The cookbook's own lifecycle, as opposed to something happening to a card.
 *
 * These were scoped to the backend — it owns the endpoints each one writes
 * through — and moved to the client when that plan was dropped. Worth knowing
 * when reading the Gherkin, which does not say which side fires what.
 *
 * `saved`/`unsaved` here are a *cookbook* being bookmarked, not an item: the
 * item-level equivalents live on `product_interaction`/`pairing_interaction`
 * alongside `added_to_cookbook`.
 */
type CookbookLifecycleAction =
  | 'created'
  | 'edited'
  | 'deleted'
  | 'saved'
  | 'unsaved';

type CookbookIdentifiedAction = CookbookCardAction | CookbookLifecycleAction;

type SharedFields = {
  /**
   * Search session id when the interaction happens on the Search tab (e.g. the
   * Cookbook filter). `null`/omitted off the Search tab.
   */
  searchId?: string | null;
  /**
   * The delivery the interaction happened against — the spec's "current
   * delivery week / fulfillment cycle". Carried by `product_interaction` and
   * `pairing_interaction` already; cookbook events were the odd one out and
   * landed in the warehouse with no `order_id` to group or join on.
   */
  orderId?: number;
};

type CookbookIdentifiedParams = FeedInteractionFields &
  SharedFields & {
    actionType: CookbookIdentifiedAction;
    /**
     * `null` when the cookbook has no numeric id to send. The default
     * ("{Name}'s Saves") cookbook is the case that matters:
     * `FavoritesCookbookV3.id` is staging-only and absent in production, so a
     * `viewed` on it would otherwise have to invent one. The spec types this
     * field nullable.
     */
    cookbookId: number | null;
    cookbookName: string;
  };

type CookbookCreateFlowParams = FeedInteractionFields &
  SharedFields & {
    actionType: 'create_flow_started';
  };

type CookbookInteractionParams =
  | CookbookIdentifiedParams
  | CookbookCreateFlowParams;

/**
 * Fires the `cookbook_interaction` event (Exp 242/252) for a cookbook card
 * viewed (≥50% viewport) or clicked, and for the start of the create flow.
 * Cookbooks surface on home, search, and the cookbook tab — `tab` distinguishes
 * them; it also fires when the user taps the Cookbook filter in Search. Reuses
 * the shared `FeedInteractionFields` (tab, container, positions, impression_id,
 * cookbook_owner_id, …). Segment stamps the `timestamp`.
 *
 * `create_flow_started` (WEB-7621) is deliberately identity-free: it fires when
 * the create sheet opens, before a cookbook exists, so the union has no
 * `cookbookId`/`cookbookName` on that arm. It is the only signal for a customer
 * who opens the sheet and abandons — a create that never completes writes no
 * backend row, so this cannot be recovered server-side.
 *
 * `tab` falls back to the current route via `resolveInteractionTab`, because the
 * create flow starts from two surfaces on different tabs (the Cookbook tab's
 * index and the add-to-cookbook sheet, which opens over Home and Search) and
 * neither should have to thread a prop for it. An explicit `tab` still wins.
 *
 * `tabSessionId` falls back the same way, off the resolved tab. The spec marks
 * it required, and a caller that had to derive its own `tab` has no prop to
 * derive the session from either — so the two have to resolve together or the
 * event lands without the id that joins it to the rest of the tab session.
 */
export const cookbookInteraction = (params: CookbookInteractionParams) => {
  const { actionType, searchId, orderId, ...feedFields } = params;

  // Narrowed off `params` rather than the destructured `actionType` so the
  // identity arm's fields are visible to the compiler.
  const cookbookIdentity =
    params.actionType === 'create_flow_started'
      ? undefined
      : { cookbook_id: params.cookbookId, cookbook_name: params.cookbookName };

  const tab = resolveInteractionTab(feedFields.tab);

  analytics.track('Cookbook Interaction', {
    ...cookbookIdentity,
    action_type: actionType,
    ...(searchId !== undefined && { search_id: searchId }),
    ...(orderId !== undefined && { order_id: orderId }),
    client: 'web',
    ...buildFeedInteractionPayload({
      ...feedFields,
      tab,
      tabSessionId: feedFields.tabSessionId ?? getTabSessionIdForTab(tab),
    }),
  });
};
